/**
 * UpstreamEcho — read-only display of a value captured upstream.
 *
 * Sprint K. The build-logic preview promises "captured once, satisfies
 * all" — every place the same fact would otherwise be re-asked
 * downstream now renders this component instead. The user sees the
 * value with a small ✏️ that jumps to the canonical capture stage so
 * edits are explicit and routed through the upstream owner.
 *
 * Usage:
 *   <UpstreamEcho
 *     label="Date of Birth"
 *     value={epcr.patient_dob}
 *     editPath={`/incidents/${incidentId}/epcr/${epcr.id}?tab=patient`}
 *   />
 */

import { Pencil, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Props {
  label: string;
  /** Value to display; null/empty shows a soft 'not captured' hint. */
  value: string | number | null | undefined;
  /** Optional formatter for non-string values (e.g. dates, codes). */
  format?: (v: NonNullable<Props['value']>) => string;
  /** Where the canonical capture lives. Used by the edit pencil. */
  editPath?: string;
  /** Friendly name of the canonical stage ('Patient tab', 'Dispatch tab'). */
  editLabel?: string;
  /** Disable the edit affordance entirely (e.g. ePCR locked). */
  locked?: boolean;
}

export function UpstreamEcho({ label, value, format, editPath, editLabel, locked }: Props) {
  const navigate = useNavigate();
  const display =
    value === null || value === undefined || value === '' ? null
    : format ? format(value) : String(value);

  return (
    <div className="border border-slate-200 bg-slate-50 rounded px-3 py-2 flex items-baseline gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
          {label}
        </div>
        <div className={`mt-0.5 text-sm font-medium ${display ? 'text-slate-900' : 'text-slate-400 italic'}`}>
          {display ?? 'not captured'}
        </div>
      </div>
      {editPath && !locked && (
        <button
          type="button"
          onClick={() => navigate(editPath)}
          className="inline-flex items-center gap-1 text-[11px] text-blue-700 hover:text-blue-900 font-semibold whitespace-nowrap"
          title={editLabel ? `Edit on ${editLabel}` : 'Edit upstream'}
        >
          <Pencil size={11} />
          {editLabel ? `Edit on ${editLabel}` : 'Edit upstream'}
        </button>
      )}
      {locked && (
        <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
          <Lock size={11} /> Locked
        </span>
      )}
    </div>
  );
}
